import { Node } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import ContentBlockNodeView from "./ContentBlockNodeView";

/**
 * InlineContentBlockNode 扩展
 * 用于识别和渲染模板中的行内内容块，支持行内插入和拖拽
 * 与 ContentBlockNode 的区别：可以在段落内部插入，作为行内元素存在，支持行内拖拽
 * V2.5: 支持instanceId，实现内容块实例管理
 */
export const InlineContentBlockNode = Node.create({
    name: "inlineContentBlock",
    group: "inline", // 关键修改：设置为行内组
    atom: true, // 将节点作为一个整体处理
    draggable: true, // 启用拖拽功能
    inline: true, // 关键修改：设置为行内节点
    selectable: true,

    // 定义节点属性（与 ContentBlockNode 保持一致）
    addAttributes() {
        return {
            // V2.5 新增：实例ID，用于唯一标识每个内容块实例
            instanceId: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-instance-id"),
                renderHTML: (attributes) => {
                    if (!attributes.instanceId) {
                        return {};
                    }
                    return {
                        "data-instance-id": attributes.instanceId,
                    };
                },
            },
            // 内容块类型，用于关联到 SidePanel 中的对应组件
            contentBlockType: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-content-block-type"),
                renderHTML: (attributes) => {
                    if (!attributes.contentBlockType) {
                        return {};
                    }
                    return {
                        "data-content-block-type": attributes.contentBlockType,
                    };
                },
            },
            // 内容块唯一ID（业务层面的ID，区别于instanceId）
            contentBlockId: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-content-block-id"),
                renderHTML: (attributes) => {
                    if (!attributes.contentBlockId) {
                        return {};
                    }
                    return {
                        "data-content-block-id": attributes.contentBlockId,
                    };
                },
            },
            // 内容块显示标签
            contentBlockLabel: {
                default: "点击右侧插入内容",
                parseHTML: (element) => element.getAttribute("data-content-block-label"),
                renderHTML: (attributes) => {
                    if (!attributes.contentBlockLabel) {
                        return {};
                    }
                    return {
                        "data-content-block-label": attributes.contentBlockLabel,
                    };
                },
            },
            // 数据键，用于指定使用哪个数据项
            dataKey: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-key"),
                renderHTML: (attributes) => {
                    if (!attributes.dataKey) {
                        return {};
                    }
                    return {
                        "data-key": attributes.dataKey,
                    };
                },
            },
            // 内容块状态：pending(待填充) | filled(已填充)
            status: {
                default: "pending",
            },
            // 新增：标识这是行内类型的内容块
            isInline: {
                default: true,
            },
            // V2.5 新增：显示模式
            viewMode: {
                default: "summary",
                parseHTML: (element) => element.getAttribute("data-view-mode"),
                renderHTML: (attributes) => {
                    if (!attributes.viewMode) {
                        return {};
                    }
                    return {
                        "data-view-mode": attributes.viewMode,
                    };
                },
            },
            filterRevision: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-filter-revision"),
                renderHTML: (attributes) => {
                    if (!attributes.filterRevision) {
                        return {};
                    }
                    return {
                        "data-filter-revision": attributes.filterRevision,
                    };
                },
            },
        };
    },

    // 解析HTML时如何识别此节点类型（修改为识别 span 标签）
    parseHTML() {
        return [
            {
                tag: "span[data-content-block-type]",
                // 优先级高于 TextStyle mark (默认50)，防止带 style 的内容块 <span> 被抢先解析为 textStyle
                priority: 60,
                getAttrs: (element) => {
                    const instanceId = element.getAttribute("data-instance-id");
                    const contentBlockType = element.getAttribute("data-content-block-type");
                    const contentBlockId = element.getAttribute("data-content-block-id");
                    const contentBlockLabel = element.getAttribute("data-content-block-label");
                    const dataKey = element.getAttribute("data-key");
                    const viewMode = element.getAttribute("data-view-mode");
                    const filterRevision = element.getAttribute("data-filter-revision");

                    return {
                        instanceId,
                        contentBlockType,
                        contentBlockId,
                        contentBlockLabel: contentBlockLabel || "点击右侧插入内容",
                        dataKey,
                        viewMode: viewMode || "summary",
                        filterRevision,
                        status: "pending",
                        isInline: true,
                    };
                },
            },
        ];
    },

    // 渲染为HTML（修改为使用 span 标签）
    renderHTML({ HTMLAttributes, node }) {
        const { instanceId, contentBlockType, contentBlockId, contentBlockLabel, dataKey, status, viewMode, filterRevision } = node.attrs;

        const attrs = {
            "data-content-block-type": contentBlockType,
            "data-content-block-label": contentBlockLabel,
            "data-status": status,
            class: `inlineContentBlock-node inlineContentBlock-${status}`,
            ...HTMLAttributes,
        };

        if (instanceId) {
            attrs["data-instance-id"] = instanceId;
        }

        if (contentBlockId) {
            attrs["data-content-block-id"] = contentBlockId;
        }

        if (dataKey) {
            attrs["data-key"] = dataKey;
        }

        if (viewMode) {
            attrs["data-view-mode"] = viewMode;
        }

        if (filterRevision) {
            attrs["data-filter-revision"] = filterRevision;
        }

        return ["span", attrs, contentBlockLabel || "点击右侧插入内容"];
    },

    // 使用自定义React组件渲染节点
    addNodeView() {
        return ReactNodeViewRenderer(ContentBlockNodeView);
    },

    // 添加命令
    addCommands() {
        return {
            // 插入行内内容块
            insertInlineContentBlock:
                (options = {}) =>
                ({ chain }) => {
                    const { instanceId, contentBlockType, contentBlockLabel } = options;

                    return chain()
                        .insertContent({
                            type: this.name,
                            attrs: {
                                instanceId,
                                contentBlockType,
                                contentBlockLabel: contentBlockLabel || "点击右侧插入内容",
                                status: "pending",
                                isInline: true,
                            },
                        })
                        .run();
                },

            // V2.5 新增：更新行内内容块实例属性
            updateInlineContentBlockInstance:
                (instanceId, attrs) =>
                ({ tr, state, dispatch }) => {
                    let updated = false;

                    state.doc.descendants((node, pos) => {
                        if (node.type.name === this.name && node.attrs.instanceId === instanceId && !updated) {
                            tr.setNodeMarkup(pos, null, {
                                ...node.attrs,
                                ...attrs,
                            });
                            updated = true;
                        }
                    });

                    if (updated && dispatch) {
                        dispatch(tr);
                        return true;
                    }

                    return false;
                },

            // 替换行内内容块为实际内容
            replaceInlineContentBlock:
                (contentBlockType, content) =>
                ({ tr, state, dispatch }) => {
                    let replaced = false;

                    state.doc.descendants((node, pos) => {
                        if (node.type.name === this.name && node.attrs.contentBlockType === contentBlockType && node.attrs.status === "pending") {
                            // 找到第一个匹配的行内内容块并替换
                            if (!replaced) {
                                const from = pos;
                                const to = pos + node.nodeSize;

                                // 对于行内内容块，直接替换为文本内容
                                if (typeof content === "string") {
                                    tr.replaceWith(from, to, state.schema.text(content));
                                } else {
                                    // 如果是复杂内容，转换为文本
                                    const textContent = typeof content === "object" ? JSON.stringify(content) : String(content);
                                    tr.replaceWith(from, to, state.schema.text(textContent));
                                }

                                replaced = true;
                            }
                        }
                    });

                    if (replaced && dispatch) {
                        dispatch(tr);
                        return true;
                    }

                    return false;
                },
        };
    },
});

export default InlineContentBlockNode;
